import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#8B7DFF', // Keep lighter purple for UI elements
      light: '#B3A7FF',
      dark: 'rgb(12, 19, 46)', // Dark navy for sidebar/logo background
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#F5F5F5', // Light gray instead of gold for minimalist approach
      light: '#FAFAFA',
      dark: '#E0E0E0',
      contrastText: 'rgba(0, 0, 0, 0.87)',
    },
    background: {
      default: '#FAFAFA', // Clean light gray background
      paper: '#ffffff',
    },
    text: {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: 'rgba(0, 0, 0, 0.6)',
    },
    success: {
      main: '#2e7d32',
    },
    error: {
      main: '#d32f2f',
    },
    warning: {
      main: '#ff9800',
    },
    info: {
      main: '#8B7DFF', // Using lighter primary color for info
    },
  },
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h5: {
      fontWeight: 500,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 500,
      fontSize: '1rem',
    },
    button: {
      fontWeight: 600,
      fontSize: '0.875rem',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 12,
          fontWeight: 600,
          padding: '10px 24px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(70, 43, 193, 0.15)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, rgb(70, 43, 193) 0%, rgb(104, 83, 215) 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, rgb(50, 30, 140) 0%, rgb(70, 43, 193) 100%)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
        },
        elevation3: {
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, rgb(70, 43, 193) 0%, rgb(104, 83, 215) 100%)',
          boxShadow: '0 4px 20px rgba(70, 43, 193, 0.15)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: '#ffffff',
          borderRight: '1px solid rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgb(70, 43, 193)',
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderRadius: 0, // Remove rounded corners
          boxShadow: 'none',
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0, // Remove rounded corners
          borderRight: '1px solid rgba(0, 0, 0, 0.12)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8, // Minimal border radius for cards
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.12)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6, // Minimal border radius
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6, // Minimal border radius
          fontWeight: 500,
        },
        colorPrimary: {
          backgroundColor: '#8B7DFF',
          color: '#ffffff',
        },
        colorSecondary: {
          backgroundColor: '#F5F5F5',
          color: 'rgba(0, 0, 0, 0.87)',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 0, // Remove rounded corners from navigation
          margin: 0,
          '&.Mui-selected': {
            backgroundColor: 'rgba(139, 125, 255, 0.15)', // Light purple overlay
            borderRight: '3px solid #8B7DFF',
            '&:hover': {
              backgroundColor: 'rgba(139, 125, 255, 0.2)',
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)', // White overlay on dark background
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          borderRadius: 8, // Minimal border radius
          border: '1px solid rgba(0, 0, 0, 0.06)',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'rgba(139, 125, 255, 0.05)',
            borderBottom: '1px solid rgba(139, 125, 255, 0.1)',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'rgba(139, 125, 255, 0.04)',
          },
        },
      },
    },
  },
});

export default theme;
